import { TableIds } from "@/store/tables";
import { useAdvertisementControllerGetAdvertisementHistory } from "@/api/types/main";
import { useCommersCardProps } from "@/hooks/useCommersCardProps";
import { useAdsCardColumns } from "@/components/tables/commers/cards/ads/ads.card.columns";
import { AdsCardTable } from "@/components/tables/commers/cards/ads/ads.card.table";
import { LoadingSpinner } from "@/components/custom-ui/loading.spinner";
import {
  ProductAdvertisementHistoryByCategoryDTOCompany,
  ProductAdvertisementHistoryDTO,
  ProductAdvertisementStatisticDTO,
} from "@/api/types/main/schemas";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";

import { TransformCardData } from "./commers.card.rating";
import { formatNumber } from "@/lib/utils";
import { format } from "date-fns";

const ADS_COMPANIES_READABLE_LABELS_PARAMS: Record<keyof ProductAdvertisementHistoryDTO, string> = {
  acos: "ACOS",
  ctr: "CTR",
  budget: "Бюджет",
  balance: "Баланс",
  spent: "Расходы",
  clicks: "Клики",
  ordersFromAds: "Заказы с рекламы",
  revenue: "Выручка",
  tacos: "TACOS",
  views: "Показы",
};

const ADS_COMPANIES_READABLE_LABELS_STATISTIC: Record<
  keyof ProductAdvertisementStatisticDTO,
  string
> = {
  acos: "ACOS",
  ctr: "CTR",
  spent: "Расходы",
  tacos: "TACOS",
};

const ADS_COMPANIES_READABLE_LABELS: {
  [key in ProductAdvertisementHistoryByCategoryDTOCompany]: string;
} = {
  BANNER_BRAND: "Баннер (Бренд)",
  BANNER_NOMENCLATURE: "Баннер (Номенклатура)",
  CAROUSEL_BRAND: "Карусель (Бренд)",
  CAROUSEL_NOMENCLATURE: "Карусель (Номенклатура)",
  CATALOG: "Каталог",
  CAROUSEL_BRAND_AUCTION: "Карусель (Бренд) Аукцион",
  SEARCH: "Поиск",
  RECOMMENDATION: "Рекомендации",
  AUTOMATIC: "Автоматическая",
  SEARCH_CATALOG: "Аукцион",
};

export interface TestAdsTdProps {
  company: string;
  params: TransformCardData[];
}

interface AdsCardStatistic {
  label: string;
  value: number;
}

interface AdsCardData {
  history: TestAdsTdProps[];
  statistic: AdsCardStatistic[];
}

export const CommersCardAds = () => {
  const { productId, dateRange, isSidebarVisible, savedSettings } = useCommersCardProps({
    TableId: TableIds.Commers_AdsCard,
  });

  const { data, isLoading, isError, error } = useAdvertisementControllerGetAdvertisementHistory(
    productId,
    {
      periodEnd: format(dateRange.to, "yyyy-MM-dd"),
      periodStart: format(dateRange.from, "yyyy-MM-dd"),
    },
    {
      query: {
        queryKey: ["card", "ads", "history", dateRange.from, dateRange.to],
        select(data) {
          try {
            const newData: AdsCardData = {
              history: [],
              statistic: [],
            };

            // Получаем реальные ключи статистики
            const statisticFields = Object.keys(
              data.statistic
            ) as (keyof ProductAdvertisementStatisticDTO)[];

            // Преобразуем
            const transformedStatistic = statisticFields.map((field) => ({
              label: ADS_COMPANIES_READABLE_LABELS_STATISTIC[field] ?? "Значение не добавлено",
              value: data.statistic[field] ?? "0",
            }));

            const transformedHistory = data.history.map((historyItem) => {
              const paramFields = Object.keys(
                historyItem.params
              ) as (keyof ProductAdvertisementHistoryDTO)[];
              const transformedParams = paramFields.map((field) => ({
                label: ADS_COMPANIES_READABLE_LABELS_PARAMS[field],
                data: historyItem.params[field],
              }));

              return {
                company:
                  ADS_COMPANIES_READABLE_LABELS[historyItem.company] || "Общие рекламные данные",
                params: transformedParams.sort((a, b) => a.label.localeCompare(b.label)),
              };
            });

            newData.history = transformedHistory;
            newData.statistic = transformedStatistic;

            return newData;
          } catch (err) {
            console.error("Ошибка трансформации данных:", err);
            throw new Error("Ошибка трансформации данных");
          }
        },
      },
    }
  );

  const columns = useAdsCardColumns(dateRange);

  if (isLoading) return <LoadingSpinner />;
  if (isError) return <div>{error?.message}</div>;

  return (
    <div className="flex flex-col h-full">
      <div className="grid grid-cols-4 gap-8 mb-5 shrink-0">
        {data?.statistic.map((statItem, index) => (
          <Card key={index}>
            <CardHeader className="flex flex-row justify-between flex-nowrap">
              <CardTitle>{statItem.label}</CardTitle>
              <CardDescription>{statItem.label === "Расходы" ? "₽" : "%"}</CardDescription>
            </CardHeader>
            <CardContent className="text-3xl font-bold overflow-hidden">
              {formatNumber(statItem.value)}
            </CardContent>
          </Card>
        ))}
      </div>

      <div className="flex-1">
        {data && (
          <AdsCardTable
            data={data.history}
            columns={columns}
            isSidebarVisible={isSidebarVisible}
            savedSettings={savedSettings}
            tableId={TableIds.Commers_AdsCard}
          />
        )}
      </div>
    </div>
  );
};
